'use client';

import { notFound } from 'next/navigation';
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Calendar, Share2, Linkedin, X, Facebook, Link as LinkIcon, Copy, ThumbsUp } from "lucide-react";
import { Author, BlogImage, type BlogPost, ImageSource, Section } from './types';
import { getBlogPost } from '../../../blog/blog-list';
import { localizeSpelling } from '@/lib/spelling-localization';
import { useState } from 'react';

// Remove the local blog posts data since we now import from blog-list.tsx
// Now using getBlogPost from blog-list.tsx instead of local data

// JSON-LD Structured Data component for SEO
function BlogJsonLd({ post, slug }: { post: BlogPost, slug: string }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "image": post.image,
    "datePublished": post.date,
    "author": {
      "@type": "Person",
      "name": post.author.name,
      "jobTitle": post.author.title
    },
    "publisher": {
      "@type": "Organization",
      "name": "OpenLabs Research",
      "logo": {
        "@type": "ImageObject",
        "url": "/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://openlabs.ai/blog/${slug}`
    },
    "keywords": post.keywords.join(", ")
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

// Client-side BlogPost component
export default function BlogClient({ slug }: { slug: string }) {
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Get current locale from URL pathname
  const getCurrentLocale = () => {
    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/');
      return pathSegments[1] || 'en'; // Default to 'en' if no locale found
    }
    return 'en';
  };
  
  const currentLocale = getCurrentLocale();
  const post = getBlogPost(slug);
  
  // Function to localize text based on current locale
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  // Localize blog post content
  const localizedPost = {
    ...post,
    title: localize(post.title),
    description: localize(post.description),
    sections: post.sections.map((section: Section) => ({
      ...section,
      title: section.title ? localize(section.title) : section.title,
      content: localize(section.content),
      stats: section.stats ? section.stats.map((stat: string) => localize(stat)) : section.stats
    })),
    keywords: post.keywords.map((keyword: string) => localize(keyword))
  };
  
  // If the post doesn't exist and is returning the default, render 404
  if (post.title === "Blog Post Not Found") {
    notFound();
  }

  // Function to get the current URL properly
  const getCurrentURL = () => {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return '';
  };

  // Function to copy URL to clipboard
  const copyToClipboard = async () => {
    try {
      const currentUrl = getCurrentURL();
      await navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Social media share functions with improved URL handling
  const shareOnLinkedIn = () => {
    const url = getCurrentURL();
    const title = localizedPost.title;
    const summary = localizedPost.description;
    
    // LinkedIn sharing URL format - Updated to most reliable current format
    const text = `${title}\n\n${summary}`;
    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}%0A%0A${encodeURIComponent(url)}`;
    
    console.log('LinkedIn share URL:', linkedInUrl); // Debug log
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  const shareOnTwitter = () => {
    const url = getCurrentURL();
    const text = `${localizedPost.title}`;
    
    // Twitter/X sharing URL format - Updated and simplified
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&via=OpenLabsResearch`;
    
    console.log('Twitter share URL:', twitterUrl); // Debug log
    window.open(twitterUrl, '_blank', 'width=600,height=600');
  };

  const shareOnFacebook = () => {
    const url = getCurrentURL();
    
    // Facebook sharing URL format - Using the most reliable and simple format
    // This format works best for automatically embedding the URL
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    
    console.log('Current URL for sharing:', url);
    console.log('Facebook share URL:', facebookUrl);
    
    // Open Facebook sharing dialog
    window.open(facebookUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
  };

  // Main blog post content with proper layout that preserves the navbar
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Add BlogJsonLd for SEO */}
      <BlogJsonLd post={localizedPost} slug={slug} />

      {/* Content without top padding - blue header will handle navbar spacing */}
      <div>
        {/* Blog Header - extended to cover entire top area including navbar space */}
        <header className="bg-gradient-to-r from-blue-600 to-indigo-700 pt-20 pb-16 text-white relative z-10">
          <div className="container mx-auto max-w-4xl px-4 pt-8">
            <div className="mb-12">
              <Link href={`/${currentLocale}/blog`} className="inline-flex items-center text-sm text-blue-100 hover:text-white transition-colors">
                <ArrowLeft size={18} className="mr-2" />
                Back to Blog
              </Link>
            </div>
            <h1 className="text-4xl font-bold mb-4">{localizedPost.title}</h1>
            <p className="text-xl text-blue-100 mb-8">
              {localizedPost.description}
            </p>
            <div className="flex items-center">
              <Image 
                src={localizedPost.author.image} 
                alt={localizedPost.author.name}
                width={48} 
                height={48} 
                className="rounded-full mr-4"
              />
              <div>
                <p className="font-medium">{localizedPost.author.name}</p>
                <p className="text-sm text-blue-200">{localizedPost.author.title}</p>
              </div>
            </div>
            <div className="flex items-center mt-6">
              <span className="inline-flex items-center mr-6 text-sm">
                <Calendar size={16} className="mr-2" />
                {localizedPost.date}
              </span>
              <span className="inline-flex items-center text-sm">
                <Clock size={16} className="mr-2" />
                {localizedPost.readTime}
              </span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="container mx-auto max-w-4xl px-4 py-12">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden -mt-16 relative z-20">
          {/* Featured Image */}
          <div className="relative h-80 w-full">
            <Image
              src={localizedPost.image}
              alt={localizedPost.title}
              fill
              className="object-cover"
            />
          </div>

          {/* Article Content */}
          <article className="p-8">
            {/* Share/Social Media Buttons */}
            <div className="flex flex-wrap justify-end mb-6 gap-2">
              {/* Copy URL Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center"
                onClick={copyToClipboard}
              >
                {copySuccess ? (
                  <>
                    <Copy size={16} className="mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 size={16} className="mr-2" />
                    Share URL
                  </>
                )}
              </Button>

              {/* LinkedIn Share */}
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                onClick={shareOnLinkedIn}
              >
                <Linkedin size={16} className="mr-2" />
                LinkedIn
              </Button>

              {/* Twitter/X Share */}
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200"
                onClick={shareOnTwitter}
              >
                <X size={16} className="mr-2" />
                X
              </Button>

              {/* Facebook Share Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                onClick={shareOnFacebook}
              >
                <Facebook size={16} className="mr-2" />
                Facebook
              </Button>
            </div>

            {/* Sections */}
            {localizedPost.sections.map((section: Section, index: number) => (
              <div key={index} className="mb-8">
                {section.title && (
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
                )}
                <div className="prose prose-blue max-w-none">
                  <p className="whitespace-pre-line">{section.content}</p>
                </div>

                {section.image && (
                  <div className="mt-6 relative">
                    <div className="relative h-64 w-full">
                      <Image
                        src={section.image.src}
                        alt={section.image.alt}
                        fill
                        className="object-contain"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {section.image.alt} 
                      {section.image.source && (
                        <span className="block">
                          Source: <a href={section.image.source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{section.image.source.text}</a>
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {section.stats && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {section.stats.map((stat: string, i: number) => (
                      <div key={i} className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-800">{stat}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Like Button */}
            <div className="border-t border-gray-200 pt-6 mt-10">
              <Button variant="outline" size="lg" className="w-full flex items-center justify-center">
                <ThumbsUp size={18} className="mr-2" />
                Found this valuable
              </Button>
            </div>

            {/* Tags */}
            <div className="mt-8">
              <h3 className="text-lg font-medium mb-3">Topics</h3>
              <div className="flex flex-wrap gap-2">
                {localizedPost.keywords.slice(0, 6).map((keyword: string, index: number) => (
                  <span key={index} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mt-10 border border-blue-100">
              <h3 className="text-xl font-bold text-blue-800 mb-2">Start Your AI Journey Today</h3>
              <p className="text-blue-700 mb-4">
                Ready to transform your business with cutting-edge AI solutions? Contact our team of experts to discuss your project.
              </p>
              <Link href={`/${currentLocale}/contact`} className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md transition-colors">
                Schedule a Consultation
              </Link>
            </div>
          </article>
        </div>
      </div>
      </div>
    </div>
  );
} 